import React, { useState } from 'react';
import { Avatar, InputBase, Paper, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useLocation } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import { selectedBranchAtom, userDataAtom } from '../../Atoms';
import useTranslate from '../../Hooks/useTranslate';
import capitalize from '../../Helpers/StringsFormatting/capitalize';
import { CenteredFlexBox, ColumnFlexBox, FlexBox } from '../FlexBox';
import useMobileNavigation from './useMobileNavigation';
import BranchSheet from './BranchSheet';

const getInitials = (userData) =>
  `${userData?.name?.[0] ?? ''}${userData?.surname?.[0] ?? ''}`.toUpperCase();

/**
 * MobileHeader — sticky, prop-driven header rendered by each screen. Common
 * chrome (branch chip → BranchSheet, title, avatar) is automatic; per-section
 * extras come in as props (see MOBILE_PLAN.md §D):
 *
 *   title    — string; defaults to the route-derived title
 *   subtitle — node; e.g. counters "12 da gestire · 3 urgenti"
 *   search   — { value, onChange, placeholder }; renders a search field
 *   actions  — node; trailing controls next to the search (filter button…)
 *   extra    — node; an arbitrary row below (segmented control, chips…)
 *   children — node; alias for `extra` when you prefer JSX nesting
 */
const MobileHeader = function MobileHeader({
  title = null,
  subtitle = null,
  search = null,
  actions = null,
  extra = null,
  children = null,
  avatar = null,
  onTitleClick = null,
}) {
  const theme = useTheme();
  const translate = useTranslate();
  const { pathname } = useLocation();
  const selectedBranch = useAtomValue(selectedBranchAtom);
  const userData = useAtomValue(userDataAtom);
  const [branchSheetOpen, setBranchSheetOpen] = useState(false);
  const { findActiveTab } = useMobileNavigation();

  const activeTab = findActiveTab(pathname);
  const resolvedTitle = title ?? translate(activeTab?.labelKey ?? 'PRIORITIES');
  const branchName = selectedBranch?.branchDescription ?? '';
  const below = extra ?? children;

  return (
    <Paper
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 3,
        px: 2.5,
        pt: 1.5,
        pb: 1.75,
        borderRadius: theme.custom.mobile.headerRadius,
      }}
    >
      {branchName ? (
        <CenteredFlexBox
          onClick={() => setBranchSheetOpen(true)}
          sx={{
            gap: 0.5,
            mb: 0.75,
            color: 'primary.main',
            cursor: 'pointer',
            width: 'fit-content',
          }}
        >
          <ApartmentRoundedIcon sx={{ fontSize: 16 }} />
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            {capitalize(branchName)}
          </Typography>
          <KeyboardArrowDownRoundedIcon
            sx={{ fontSize: 18, color: theme.custom.mobile.mutedColor }}
          />
        </CenteredFlexBox>
      ) : null}

      <CenteredFlexBox sx={{ justifyContent: 'space-between', gap: 1 }}>
        <ColumnFlexBox sx={{ minWidth: 0 }}>
          <CenteredFlexBox
            onClick={onTitleClick ?? undefined}
            sx={{
              gap: 0.25,
              minWidth: 0,
              cursor: onTitleClick ? 'pointer' : 'default',
            }}
          >
            <Typography
              noWrap
              sx={{ fontSize: 26, fontWeight: 700, letterSpacing: '-.01em' }}
            >
              {resolvedTitle}
            </Typography>
            {onTitleClick ? (
              <KeyboardArrowDownRoundedIcon
                sx={{ fontSize: 26, color: 'primary.main', flex: 'none' }}
              />
            ) : null}
          </CenteredFlexBox>
          {subtitle ? (
            <Typography
              variant="body2"
              sx={{ color: theme.custom.mobile.mutedColor }}
            >
              {subtitle}
            </Typography>
          ) : null}
        </ColumnFlexBox>
        <FlexBox sx={{ alignItems: 'center' }}>
          {avatar ?? (
            <Avatar
              sx={{
                width: 44,
                height: 44,
                fontSize: 15,
                fontWeight: 700,
                color: '#fff',
                background: theme.custom.mobile.brandGradient,
              }}
            >
              {getInitials(userData)}
            </Avatar>
          )}
        </FlexBox>
      </CenteredFlexBox>

      {search || actions ? (
        <CenteredFlexBox sx={{ gap: 1, mt: 1.5 }}>
          {search ? (
            <CenteredFlexBox
              sx={{
                flex: 1,
                gap: 1,
                height: 46,
                px: 1.5,
                borderRadius: '14px',
                bgcolor: 'background.default',
              }}
            >
              <SearchRoundedIcon
                sx={{ color: theme.custom.mobile.mutedColor }}
              />
              <InputBase
                fullWidth
                value={search.value ?? ''}
                onChange={search.onChange}
                placeholder={search.placeholder ?? translate('SEARCH')}
                sx={{ fontSize: 15 }}
              />
            </CenteredFlexBox>
          ) : null}
          {actions}
        </CenteredFlexBox>
      ) : null}

      {below ? <FlexBox sx={{ mt: 1.5 }}>{below}</FlexBox> : null}

      <BranchSheet
        open={branchSheetOpen}
        onClose={() => setBranchSheetOpen(false)}
      />
    </Paper>
  );
};

export default MobileHeader;
